import { Injectable, computed, inject, signal } from '@angular/core';
import { STAFF_NO_ENTRY_MESSAGE, STAFF_PERMISSION_REQUIRED_MESSAGE } from './staff-authz';
import { STAFF_OIDC_AUTH_CONFIG } from './staff-oidc-auth.config';
import { StaffOidcAuthService } from './staff-oidc-auth.service';

/**
 * 세션 권한 헬퍼 — `me` 의 `scopes`·`canManageStaff` 를 화면이 읽는다. **표시용일 뿐**이다:
 * 서버는 라우트마다 따로 거부하며, `has()` 가 `true` 여도 세션 scope 가 오래됐거나 권한이
 * 회수됐으면 403 이 날 수 있다(그 경우 `staffPermissionDeniedInterceptor` 가 `denial` 에 안내를 올린다).
 *
 * `provideStaffOidcAuth({ authz })` 가 없거나 `me` 가 아직 없으면 **모든 scope 를 허용**한다 — 비활성하지 않고 서버 판단에
 * 맡겨, 인가 미도입 앱이 이 lib 을 올려도 화면이 잠기지 않게 한다.
 */
@Injectable({ providedIn: 'root' })
export class StaffAuthzService {
  private readonly auth = inject(StaffOidcAuthService);

  private readonly authz = inject(STAFF_OIDC_AUTH_CONFIG).authz;

  readonly canManageStaff = computed(() => {
    const me = this.auth.me();
    return this.authz !== null && me !== null && this.authz.canManageStaff(me);
  });

  /** 저장된 초대 상태 — 인가 설정이 없거나 `me` 가 아직 없거나 앱이 초대를 도입하지 않았으면 `null`. */
  readonly invitationStatus = computed(() => {
    const me = this.auth.me();
    if (this.authz === null || me === null || !this.authz.invitationStatus) return null;
    return this.authz.invitationStatus(me);
  });

  /**
   * 초대를 수락했는가(또는 초대가 필요 없는 staff 인가). 상태를 모르면 `true` — 화면이 서버 판단보다 앞서 사용자를 막지 않는다.
   * `false` 는 `Pending`·`Rejected`·`Cancelled`·`Expired` 이고, 이때 서버는 scope 요구를 전부 거부한다.
   */
  readonly accepted = computed(() => {
    const status = this.invitationStatus();
    return status === null || status === 'Accepted';
  });

  /** 본인의 로컬 `Staff.id` — 인가 설정이 없거나 `me` 가 아직 없으면 `null`. 관리 화면이 목록에서 본인 행을 가려낸다. */
  readonly staffId = computed(() => {
    const me = this.auth.me();
    if (this.authz === null || me === null) return null;
    return this.authz.staffId(me);
  });

  /** 가장 최근 인가 거부 안내 — 앱 셸이 배너로 보인다. `at` 은 같은 문구가 다시 와도 갱신을 알리는 용도. */
  readonly denial = signal<{ message: string; at: number } | null>(null);

  has(scope: string): boolean {
    const me = this.auth.me();
    return this.authz === null || me === null || this.authz.hasScope(me, scope);
  }

  /** 모든 scope 를 가졌는가 — 한 화면이 여러 능력을 요구할 때. */
  hasAll(...scopes: string[]): boolean {
    return scopes.every((s) => this.has(s));
  }

  /** 비활성 버튼 툴팁용. 권한이 있으면 빈 문자열(툴팁 없음). */
  requiredMessage(...scopes: string[]): string {
    return this.hasAll(...scopes) ? '' : STAFF_PERMISSION_REQUIRED_MESSAGE;
  }

  /** 서버가 인가 거부(403 `staff-scope-denied:<reason>`)를 줬을 때 호출한다. */
  reportDenied(reason: string): void {
    const message =
      reason === 'no-entry' ? STAFF_NO_ENTRY_MESSAGE : STAFF_PERMISSION_REQUIRED_MESSAGE;
    this.denial.set({ message, at: Date.now() });
  }

  clearDenial(): void {
    this.denial.set(null);
  }
}
